import { ATCStackFrameStatus } from '@cotera/api';
import { clsx } from 'clsx';
import type { ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * The app's focus treatment, lifted from the `focus-visible:` half of
 * `button.tsx`'s variants.
 *
 * For controls that are bare `<button>`s, `<a>`s or Radix triggers rather than
 * `<Button>`s — breadcrumb crumbs, the artifacts index card's rows, a `…` menu
 * trigger. Those get no focus ring by default, so without this they are
 * keyboard-reachable and invisibly focused.
 *
 * Shared because it was copied into five files at once and the copies had
 * already drifted: one `…` menu trigger carried the ring and its twin, built
 * from the same class string, did not.
 */
export const FOCUS_RING =
  'outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]';

const isMac = () => {
  return /Mac|iPod|iPhone|iPad/.test(navigator.platform);
};

export const getCmdModifierKeySymbol = () => {
  return isMac() ? '⌘' : 'Ctrl';
};

const SYMBOL_MAP = {
  ctrl: getCmdModifierKeySymbol(),
  meta: getCmdModifierKeySymbol(),
  shift: '⇧',
  alt: '⌥',
  cmd: getCmdModifierKeySymbol(),
  command: getCmdModifierKeySymbol(),
};

/** One key as it should read on screen: `meta` -> `⌘`, `n` -> `N`. */
export const displayKey = (key: string): string =>
  SYMBOL_MAP[key as keyof typeof SYMBOL_MAP] ||
  (key.length === 1 ? key.toUpperCase() : key);

export const displayShortcut = (shortcut: string[]) =>
  shortcut.map(displayKey).join('+');

/**
 * Gets the appropriate Tailwind classes for a run status (full badge styling)
 */
export const getRunStatusClassName = (status: ATCStackFrameStatus): string => {
  switch (status) {
    case 'completed':
      return 'bg-emerald-600/70 text-white font-semibold [&>svg]:text-white [a&]:hover:bg-emerald-700 focus-visible:ring-emerald-500/20';
    case 'failed':
      return 'bg-red-600/70 text-white font-semibold [&>svg]:text-white [a&]:hover:bg-red-700 focus-visible:ring-red-500/20';
    case 'pending':
      return 'bg-blue-600/70 text-white font-semibold [&>svg]:text-white [a&]:hover:bg-blue-700 focus-visible:ring-blue-500/20';
    case 'running':
      return 'bg-cyan-600/70 text-white font-semibold [&>svg]:text-white [a&]:hover:bg-cyan-700 focus-visible:ring-cyan-500/20';
    case 'cancelled':
      return 'bg-amber-600/70 text-white font-semibold [&>svg]:text-white [a&]:hover:bg-amber-700 focus-visible:ring-amber-500/20';
    case 'allocated':
      return 'bg-indigo-600/70 text-white font-semibold [&>svg]:text-white [a&]:hover:bg-indigo-700 focus-visible:ring-indigo-500/20';
    case 'queued':
      return 'bg-violet-600/70 text-white font-semibold [&>svg]:text-white [a&]:hover:bg-violet-700 focus-visible:ring-violet-500/20';
    case 'stalled':
      return 'bg-orange-600/70 text-white font-semibold [&>svg]:text-white [a&]:hover:bg-orange-700 focus-visible:ring-orange-500/20';
    case 'waiting':
      return 'bg-sky-600/70 text-white font-semibold [&>svg]:text-white [a&]:hover:bg-sky-700 focus-visible:ring-sky-500/20';
    default:
      return 'bg-gray-600/70 text-white font-semibold [&>svg]:text-white [a&]:hover:bg-gray-700 focus-visible:ring-gray-500/20';
  }
};

/**
 * Gets the appropriate Tailwind classes for a status indicator dot (simple background color)
 */
export const getStatusIndicatorClassName = (
  status: ATCStackFrameStatus
): string => {
  return getRunStatusClassName(status);
};
